import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangleIcon,
  GlobeIcon,
  LockIcon,
  Loader2Icon,
  RefreshCwIcon,
  ShieldAlertIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import instancesApi, { VMInstanceDetails } from '@nebula/shared/services/instancesApi'
import { publicIpApi } from '@nebula/shared/services/publicIpApi'
import { listCustomerVPCs } from '@nebula/shared/services/vpcApi'
import { getApiErrorCode, getErrorMessage } from '@nebula/shared/utils'

/**
 * Card "Modo de rede" da aba Networking (issue #761).
 *
 * O modo era decidido na criação e nunca mudava; o portal não tinha onde pedir a
 * troca. Aqui ela existe nos dois sentidos — e com as duas consequências à vista
 * ANTES de confirmar, porque nenhuma delas se desfaz sozinha:
 *
 * - **exposição**: fora da VPC a VM deixa de estar atrás do NAT do Cloud Gateway
 *   e passa a ser alcançável pela internet. Vira checkbox obrigatório, do mesmo
 *   jeito que a #727 fez com a VPC sem gateway;
 * - **reinício**: o endereço novo é aplicado pelo cloud-init no boot. A operação
 *   NÃO reinicia a VM, então o aviso da API é o que diz ao cliente por que ela
 *   ainda não responde — e por isso ele fica na tela depois da troca, não some
 *   junto com o modal.
 */

interface NetworkModeCardProps {
  instance: VMInstanceDetails
}

type Modo = 'vpc' | 'public_bridge'

export default function NetworkModeCard({ instance }: NetworkModeCardProps) {
  const queryClient = useQueryClient()
  // Backend anterior à #761 não devolve o campo; 'vpc' é o default do model.
  const modoAtual: Modo = instance.network_mode === 'public_bridge' ? 'public_bridge' : 'vpc'
  const clusterId = instance.cluster?.id

  // VM fora do ar operacional (criando, excluída, em erro) não tem NIC para
  // trocar: o backend responde 404 "VM not found in any cluster node" e o cliente
  // leria um erro cru por uma ação que nunca poderia funcionar.
  // `error` entra: o backend não exige status, e é justamente uma VM nesse estado
  // que precisa poder voltar para a VPC. Ficam de fora só os estados em que não
  // há VM no node para configurar.
  const operacional = ['running', 'stopped', 'paused', 'suspended', 'error'].includes(
    (instance.status || '').toLowerCase(),
  )

  const [painel, setPainel] = useState<Modo | null>(null)
  const [ipEscolhido, setIpEscolhido] = useState('')
  const [vpcEscolhida, setVpcEscolhida] = useState('')
  const [confirmouExposicao, setConfirmouExposicao] = useState(false)
  const [confirmouSemGateway, setConfirmouSemGateway] = useState(false)
  const [avisos, setAvisos] = useState<string[]>([])

  // IPs `allocated` do próprio cliente, no cluster da VM: são os únicos que o
  // backend aceita (mesmo filtro de `_validate_public_ip_for_vm`). Oferecer
  // outros seria só um 409 esperando acontecer.
  //
  // Sem `cluster` no detalhe (payload antigo em cache), a lista vem sem filtro —
  // mesma escolha do `ChangeNetworkModal`: listar demais e deixar o backend
  // recusar é melhor que apresentar uma lista vazia e travar o cliente. O 409
  // traz mensagem própria para esse caso.
  const {
    data: ipsLivres,
    isLoading: carregandoIps,
    isError: erroIps,
  } = useQuery({
    queryKey: ['public-ips-allocated', clusterId],
    queryFn: () => publicIpApi.listAllocated(clusterId),
    enabled: painel === 'public_bridge',
  })

  const {
    data: vpcsData,
    isLoading: carregandoVpcs,
    isError: erroVpcs,
  } = useQuery({
    queryKey: ['customer-vpcs'],
    queryFn: () => listCustomerVPCs(),
    enabled: painel === 'vpc',
  })

  const vpcsElegiveis = useMemo(
    () =>
      (vpcsData?.vpcs ?? []).filter(
        (vpc) =>
          // Desabilitada responde 404 no backend; de outro cluster, a bridge nem
          // existe nos nodes onde a VM roda.
          vpc.enabled !== false && (!clusterId || vpc.cluster_id === clusterId),
      ),
    [vpcsData, clusterId],
  )

  // Resíduo de uma exposição que não concluiu: a VM está em `vpc` no portal, mas
  // ainda tem IPv4 vinculado. Esse endereço está `attached` e por isso NÃO vem em
  // `/public-ips/allocated` — sem injetá-lo aqui, o painel diria "você não tem
  // IPv4 livre, compre um", e comprar não resolve: qualquer outro endereço leva
  // 409. Refazer a exposição com ele é a única saída self-service.
  const ipPreso = modoAtual === 'vpc' ? (instance.public_ip ?? null) : null

  const ips = useMemo(() => {
    const livres = ipsLivres?.items ?? []
    if (!ipPreso) return livres
    return [
      {
        id: ipPreso.id,
        ip_address: ipPreso.ip_address,
        gateway: ipPreso.gateway ?? '',
        netmask: ipPreso.netmask ?? '',
        cluster_id: clusterId ?? '',
        retomada: true,
      },
      ...livres.filter((ip) => ip.id !== ipPreso.id),
    ]
  }, [ipsLivres, ipPreso, clusterId])

  const vpcSelecionada = vpcsElegiveis.find((vpc) => vpc.id === vpcEscolhida)

  const trocar = useMutation({
    mutationFn: (params: Parameters<typeof instancesApi.changeNetworkMode>[1]) =>
      instancesApi.changeNetworkMode(instance.id, params),
    onSuccess: (data) => {
      // Detalhe e NICs juntos: o card de IP público e a lista de interfaces leem
      // fontes diferentes, e deixar uma delas velha mostra a VM em dois estados.
      queryClient.invalidateQueries({ queryKey: ['instance', instance.id] })
      queryClient.invalidateQueries({ queryKey: ['instance-nics', instance.id] })
      queryClient.invalidateQueries({ queryKey: ['public-ips-allocated'] })
      setAvisos(data.warnings ?? [])
      fecharPainel()
    },
    onError: (erro) => {
      // A lista pode estar velha: se o gateway foi desatachado depois do load,
      // `has_gateway` mente e o checkbox nunca apareceria — o cliente levaria o
      // 400 sem saída. Mesma reação do modal de mover VM (#727).
      if (getApiErrorCode(erro) === 'vpc_without_gateway') {
        queryClient.invalidateQueries({ queryKey: ['customer-vpcs'] })
      }

      // `ip_publico_preso`: existe um vínculo residual que o seletor não mostra
      // (o IP está `attached`). Recarregar o detalhe traz `public_ip`, e com ele
      // a opção de retomada aparece na lista.
      if (getApiErrorCode(erro) === 'ip_publico_preso') {
        queryClient.invalidateQueries({ queryKey: ['instance', instance.id] })
      }

      // Erro NÃO quer dizer "nada mudou": quando o Proxmox recusa depois de já
      // ter gravado a rede nova, o backend grava o estado e devolve 500 — a VM
      // está exposta. Sem recarregar aqui, o card seguiria oferecendo "expor" e
      // a segunda tentativa levaria um 409 "já está exposta" sem explicação, até
      // o refetch de 30s da página.
      queryClient.invalidateQueries({ queryKey: ['instance', instance.id] })
      queryClient.invalidateQueries({ queryKey: ['instance-nics', instance.id] })
      queryClient.invalidateQueries({ queryKey: ['public-ips-allocated'] })
    },
  })

  const fecharPainel = () => {
    setPainel(null)
    setIpEscolhido('')
    setVpcEscolhida('')
    setConfirmouExposicao(false)
    setConfirmouSemGateway(false)
    trocar.reset()
  }

  // Uma VPC/IP só: pré-seleciona para o cliente não escolher o óbvio na mão.
  useEffect(() => {
    if (painel === 'public_bridge' && !ipEscolhido && ips.length > 0) {
      setIpEscolhido(ips[0].id)
    }
    if (painel === 'vpc' && !vpcEscolhida && vpcsElegiveis.length > 0) {
      setVpcEscolhida(vpcsElegiveis[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painel, ips.length, vpcsElegiveis.length])

  const mensagemDeErro = trocar.isError ? getErrorMessage(trocar.error) : null
  const recusadoPorFaltaDeGateway = getApiErrorCode(trocar.error) === 'vpc_without_gateway'
  const precisaConfirmarGateway = vpcSelecionada?.has_gateway === false || recusadoPorFaltaDeGateway

  const trocando = trocar.isPending
  const bloqueiaExpor = !ipEscolhido || !confirmouExposicao || trocando
  const bloqueiaVoltar =
    !vpcEscolhida || (precisaConfirmarGateway && !confirmouSemGateway) || trocando

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center text-lg font-semibold text-gray-900">
          {modoAtual === 'public_bridge' ? (
            <GlobeIcon className="mr-2 h-5 w-5 text-blue-600" />
          ) : (
            <LockIcon className="mr-2 h-5 w-5 text-purple-600" />
          )}
          Modo de Rede
        </h2>
        {painel === null && (
          <button
            onClick={() => {
              setAvisos([])
              setPainel(modoAtual === 'public_bridge' ? 'vpc' : 'public_bridge')
            }}
            disabled={!operacional}
            title={
              operacional
                ? undefined
                : 'Indisponível enquanto a VM está sendo criada ou excluída — não há o que configurar no node ainda.'
            }
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCwIcon className="h-4 w-4" />
            {modoAtual === 'public_bridge' ? 'Voltar para a VPC' : 'Expor na internet'}
          </button>
        )}
      </div>

      <p className="text-sm text-gray-600">
        {modoAtual === 'public_bridge' && instance.wan_dhcp ? (
          // Endereço por DHCP da WAN (issue #1061): não é dado nosso — não é reservado, vem do
          // lease depois do boot e pode mudar. Dizer isso aqui é o que impede o cliente de
          // montar em cima dele algo que dependa de IP fixo.
          <>
            Esta VM está <strong>diretamente na internet</strong> com endereço{' '}
            <strong>por DHCP da rede do provedor</strong>: ela não passa pelo NAT do Cloud Gateway e
            é alcançável de fora. O endereço não é reservado nem cobrado — é o que o guest agent
            reporta e pode mudar a cada renovação do lease; não o use em DNS ou em regras que
            apontam para um IP fixo.
          </>
        ) : modoAtual === 'public_bridge' ? (
          <>
            Esta VM está <strong>diretamente na internet</strong>
            {instance.public_ip ? <> com o IPv4 {instance.public_ip.ip_address}</> : null}: ela não
            passa pelo NAT do Cloud Gateway e é alcançável de fora.
          </>
        ) : (
          <>
            Esta VM está <strong>dentro da sua VPC</strong>: recebe endereço por DHCP do Cloud
            Gateway e sai para a internet pelo NAT dele, sem ser alcançável de fora.
          </>
        )}
      </p>

      {avisos.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex gap-2">
            <AlertTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <ul className="list-inside list-disc space-y-1 text-sm text-amber-900">
              {avisos.map((aviso) => (
                <li key={aviso}>{aviso}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Painel: expor na internet                                         */}
      {/* ---------------------------------------------------------------- */}
      {painel === 'public_bridge' && (
        <div className="mt-4 space-y-4 rounded-lg border border-gray-200 p-4">
          <div>
            <label htmlFor="ip-publico" className="mb-1 block text-sm font-medium text-gray-700">
              IPv4 público
            </label>
            {carregandoIps ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2Icon className="h-4 w-4 animate-spin" />
                Carregando IPs disponíveis...
              </div>
            ) : erroIps ? (
              // Falha ao listar não é "não tem": afirmar estoque zero num 403 de
              // permissão (o endpoint exige `network:read`) manda o cliente
              // comprar um IP que ele já tem.
              <p role="alert" className="text-sm text-red-700">
                Não foi possível carregar seus IPv4 públicos. Recarregue a página; se continuar,
                verifique com quem administra sua conta se você tem acesso a Rede → IPs Públicos.
              </p>
            ) : ips.length === 0 ? (
              // Sem estoque a operação não tem como acontecer — dizer onde
              // resolver vale mais que um seletor vazio.
              <p className="text-sm text-gray-600">
                Você não tem nenhum IPv4 público livre neste cluster. Compre um em{' '}
                <span className="font-medium">Rede → IPs Públicos</span> antes de expor esta VM.
              </p>
            ) : (
              <select
                id="ip-publico"
                value={ipEscolhido}
                onChange={(e) => setIpEscolhido(e.target.value)}
                disabled={trocando}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                {ips.map((ip) => (
                  <option key={ip.id} value={ip.id}>
                    {ip.ip_address}
                    {(ip as { retomada?: boolean }).retomada ? ' — concluir operação anterior' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {ipPreso && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              O IPv4 <strong>{ipPreso.ip_address}</strong> ficou vinculado a esta VM por uma
              operação que não concluiu. Exponha a VM com ele para fechar a pendência — depois disso
              você pode voltar para a VPC ou trocar de endereço normalmente.
            </div>
          )}

          <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-start gap-2">
              <ShieldAlertIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <div className="text-sm text-red-900">
                Fora da VPC a VM <strong>deixa de estar atrás do NAT do Cloud Gateway</strong> e
                passa a ser alcançável pela internet. Revise senhas e serviços expostos antes de
                continuar.
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-red-900">
              <input
                type="checkbox"
                checked={confirmouExposicao}
                onChange={(e) => setConfirmouExposicao(e.target.checked)}
                disabled={trocando}
                className="mt-1"
              />
              <span>Entendo que esta VM ficará acessível pela internet.</span>
            </label>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            O endereço é aplicado pelo cloud-init no boot, então{' '}
            <strong>a VM só responde no IP novo depois de você reiniciá-la</strong> — a troca aqui
            não reinicia nada. Conexões abertas pelo endereço antigo caem; o console do portal
            continua disponível.
          </div>

          {mensagemDeErro && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {mensagemDeErro}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={fecharPainel}
              disabled={trocando}
              className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                trocar.mutate({
                  mode: 'public_bridge',
                  publicIpAddressId: ipEscolhido,
                  confirmExposure: confirmouExposicao,
                })
              }
              disabled={bloqueiaExpor}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {trocando ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                <GlobeIcon className="h-4 w-4" />
              )}
              Expor na internet
            </button>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Painel: voltar para a VPC                                         */}
      {/* ---------------------------------------------------------------- */}
      {painel === 'vpc' && (
        <div className="mt-4 space-y-4 rounded-lg border border-gray-200 p-4">
          <div>
            <label htmlFor="vpc-destino" className="mb-1 block text-sm font-medium text-gray-700">
              VPC de destino
            </label>
            {carregandoVpcs ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2Icon className="h-4 w-4 animate-spin" />
                Carregando VPCs...
              </div>
            ) : erroVpcs ? (
              <p role="alert" className="text-sm text-red-700">
                Não foi possível carregar suas VPCs. Recarregue a página; se continuar, verifique
                com quem administra sua conta se você tem acesso a Rede &amp; Segurança.
              </p>
            ) : vpcsElegiveis.length === 0 ? (
              <p className="text-sm text-gray-600">
                Você não tem VPC no cluster desta VM. Crie uma em{' '}
                <span className="font-medium">Rede &amp; Segurança</span> antes de trazer a VM de
                volta.
              </p>
            ) : (
              <select
                id="vpc-destino"
                value={vpcEscolhida}
                onChange={(e) => {
                  setVpcEscolhida(e.target.value)
                  // Confirmação é por VPC: trocar o destino exige uma nova.
                  setConfirmouSemGateway(false)
                  trocar.reset()
                }}
                disabled={trocando}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                {vpcsElegiveis.map((vpc) => (
                  <option key={vpc.id} value={vpc.id}>
                    {vpc.name} ({vpc.cidr || 'Sem CIDR'})
                    {vpc.has_gateway === false ? ' — sem gateway' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {precisaConfirmarGateway && (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900">
                  <strong>{vpcSelecionada?.name}</strong> não tem Cloud Gateway conectado: nessa VPC
                  a VM fica sem DHCP e sem acesso à internet até um gateway ser atachado.
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={confirmouSemGateway}
                  onChange={(e) => setConfirmouSemGateway(e.target.checked)}
                  disabled={trocando}
                  className="mt-1"
                />
                <span>Entendo que a VM ficará sem DHCP e sem acesso à internet.</span>
              </label>
            </div>
          )}

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            {/* Sem afirmar DHCP: o backend devolve à VM o endereço fixo interno que
                ela tinha na VPC, quando existe — e é o aviso pós-ação que diz qual
                endereço ela recebeu. */}
            A VM volta a receber o endereço da VPC <strong>depois de reiniciada</strong> — o fixo
            que ela tinha lá, ou um por DHCP.
            {instance.wan_dhcp
              ? ' O endereço público por DHCP simplesmente deixa de existir: não havia IP reservado.'
              : ' O IPv4 público continua reservado (e cobrado) para você até ser liberado em Rede → IPs Públicos.'}
          </div>

          {mensagemDeErro && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {mensagemDeErro}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={fecharPainel}
              disabled={trocando}
              className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                trocar.mutate({
                  mode: 'vpc',
                  networkPoolId: vpcEscolhida,
                  confirmNoGateway: confirmouSemGateway,
                })
              }
              disabled={bloqueiaVoltar}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {trocando ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                <LockIcon className="h-4 w-4" />
              )}
              Voltar para a VPC
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
